import React, { useCallback, useEffect, useState } from 'react';
import {
  FileText,
  Plus,
  Pencil,
  Trash2,
  Download,
  Send,
  Copy,
  Inbox,
  Loader2,
  Lock,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/toaster.jsx';
import { cn } from '@/lib/utils';
import {
  listLclOfertas,
  createLclOferta,
  crearDesdeCotizacion,
  deleteLclOferta,
  nuevaVersionLclOferta,
  setEstadoLclOferta,
  getPdfUrlOferta,
} from '../lclOfertas.service.js';
import { listLclCotizaciones } from '../../cotizaciones/lclCotizaciones.service.js';
import { formatImporte } from '../../cotizaciones/utils/lclCotizacionCalc.js';
import LclOfertaDialog from './LclOfertaDialog.jsx';

const ESTADO_BADGE = {
  Borrador: 'bg-slate-100 text-slate-700 border-slate-200',
  Enviada: 'bg-blue-100 text-blue-800 border-blue-200',
  Aprobada: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  Rechazada: 'bg-rose-100 text-rose-800 border-rose-200',
  Vencida: 'bg-amber-100 text-amber-800 border-amber-200',
};

const formatFecha = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  const p = (n) => String(n).padStart(2, '0');
  // En UTC, igual que el PDF: una fecha sin hora leída en local se
  // corre un día y la vigencia quedaría mal.
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`;
};

/**
 * Ofertas al cliente de una solicitud LCL.
 *
 * Tiene sus propios hooks: el diálogo de detalle que la monta corta con
 * `if (!request) return null` antes de cualquier hook.
 */
export default function LclOfertasSection({ lclRequestId, lclRequest, onChanged }) {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [cotizaciones, setCotizaciones] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [editando, setEditando] = useState(null);
  const [aBorrar, setABorrar] = useState(null);
  const [creando, setCreando] = useState(false);

  const cargar = useCallback(async () => {
    if (!lclRequestId) return;
    setCargando(true);
    try {
      const [r, c] = await Promise.all([
        listLclOfertas(lclRequestId),
        listLclCotizaciones(lclRequestId).catch(() => ({ data: [] })),
      ]);
      setItems(r?.data || []);
      setCotizaciones(c?.data || []);
    } catch (e) {
      toast.error('No se pudieron cargar las ofertas', e?.message);
    } finally {
      setCargando(false);
    }
  }, [lclRequestId, toast]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  /**
   * Arma la oferta desde la cotización seleccionada si hay una; si no,
   * desde cero. Partir de la cotización elegida es el camino normal:
   * trae los conceptos y el costo de cada uno.
   */
  const crear = async () => {
    setCreando(true);
    try {
      const elegida =
        cotizaciones.find((c) => c.estado === 'Seleccionada') ||
        cotizaciones.find((c) => c.estado === 'Confirmada');

      const r = elegida
        ? await crearDesdeCotizacion({
            lclRequestId,
            cotizacionIds: [elegida._id],
            monedaBase: elegida.moneda,
            monedaSecundaria: elegida.monedaSecundaria,
            trm: elegida.trm,
          })
        : await createLclOferta({ lclRequestId });

      if (r?.success) {
        toast.success(
          'Oferta creada',
          elegida
            ? `Con los conceptos de ${elegida.numero}`
            : 'En blanco: agregá los conceptos',
        );
        await cargar();
        setEditando(r.data._id);
        onChanged?.();
      } else {
        toast.error('No se pudo crear', [].concat(r?.message || '').join(' · '));
      }
    } finally {
      setCreando(false);
    }
  };

  const versionar = async (o) => {
    const r = await nuevaVersionLclOferta(o._id);
    if (r?.success) {
      toast.success('Versión creada', `v${r.data.version}`);
      await cargar();
      setEditando(r.data._id);
    } else {
      toast.error('No se pudo versionar', r?.message);
    }
  };

  const descargar = async (o) => {
    const r = await getPdfUrlOferta(o._id);
    const url = r?.data?.url;
    if (r?.success && url) window.open(url, '_blank', 'noopener');
    else toast.error('No se pudo abrir el PDF', r?.message);
  };

  const responder = async (o, estado) => {
    const r = await setEstadoLclOferta(o._id, estado);
    if (r?.success) {
      toast.success(`Oferta ${estado.toLowerCase()}`, o.numero);
      cargar();
      onChanged?.();
    } else {
      toast.error('No se pudo cambiar el estado', r?.message);
    }
  };

  const borrar = async () => {
    if (!aBorrar) return;
    const r = await deleteLclOferta(aBorrar._id);
    if (r?.success) {
      toast.success('Oferta eliminada', aBorrar.numero);
      cargar();
      onChanged?.();
    } else {
      toast.error('No se pudo eliminar', r?.message);
    }
    setABorrar(null);
  };

  const conCotizacion = cotizaciones.some((c) =>
    ['Seleccionada', 'Confirmada'].includes(c.estado),
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          {items.length === 0
            ? 'Sin ofertas al cliente'
            : `${items.length} oferta(s)`}
          {conCotizacion && items.length === 0 && (
            <span className="ml-1">
              · se armará con los conceptos de la cotización elegida
            </span>
          )}
        </p>
        <Button size="sm" onClick={crear} disabled={creando}>
          {creando ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Plus className="mr-2 h-4 w-4" />
          )}
          Crear oferta
        </Button>
      </div>

      {cargando && (
        <div className="flex items-center justify-center py-6 text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cargando…
        </div>
      )}

      {!cargando && items.length === 0 && (
        <div className="flex flex-col items-center rounded-lg border border-dashed py-8 text-center">
          <Inbox className="mb-2 h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Todavía no armaste la oferta para el cliente.
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {conCotizacion
              ? 'Se puede partir de la cotización que elegiste y ajustar las tarifas.'
              : 'Podés armarla desde cero o cargar primero una cotización de proveedor.'}
          </p>
        </div>
      )}

      <div className="space-y-2">
        {items.map((o) => {
          const bloqueada = o.estado !== 'Borrador';
          return (
            <div key={o._id} className="rounded-lg border p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{o.numero}</span>
                    <span className="text-xs text-muted-foreground">v{o.version}</span>
                    <Badge
                      variant="outline"
                      className={cn('font-normal', ESTADO_BADGE[o.estado])}
                    >
                      {o.estado}
                    </Badge>
                    {bloqueada && (
                      <Badge
                        variant="outline"
                        className="font-normal text-muted-foreground"
                        title="Una oferta enviada no se edita: se crea una versión nueva"
                      >
                        <Lock className="mr-1 h-3 w-3" /> bloqueada
                      </Badge>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span>Vigencia: {formatFecha(o.vigenciaHasta)}</span>
                    <span>
                      {(o.conceptos || []).filter((c) => c.visibilidad !== 'excluido').length}{' '}
                      conceptos
                    </span>
                    {o.envios?.length > 0 && (
                      <span>
                        Enviada {o.envios.length}{' '}
                        {o.envios.length === 1 ? 'vez' : 'veces'}
                      </span>
                    )}
                    {o.utilidad?.margenPorcentaje != null && (
                      <span className="text-slate-500" title="Dato interno">
                        margen {o.utilidad.margenPorcentaje} %
                      </span>
                    )}
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-lg font-semibold">
                    {formatImporte(o.totalBase, o.monedaBase)}
                  </div>
                  {o.monedaSecundaria && o.totalSecundaria != null && (
                    <div className="text-[11px] text-muted-foreground">
                      {formatImporte(o.totalSecundaria, o.monedaSecundaria)}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-2 flex flex-wrap gap-1.5">
                <Button size="sm" variant="outline" onClick={() => setEditando(o._id)}>
                  <Pencil className="mr-1.5 h-3.5 w-3.5" />
                  {bloqueada ? 'Ver' : 'Editar'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => descargar(o)}>
                  <Download className="mr-1.5 h-3.5 w-3.5" /> PDF
                </Button>
                {bloqueada && (
                  <Button size="sm" variant="outline" onClick={() => versionar(o)}>
                    <Copy className="mr-1.5 h-3.5 w-3.5" /> Nueva versión
                  </Button>
                )}
                {o.estado === 'Enviada' && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-emerald-700"
                      onClick={() => responder(o, 'Aprobada')}
                    >
                      <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Aprobada
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-rose-700"
                      onClick={() => responder(o, 'Rechazada')}
                    >
                      <XCircle className="mr-1.5 h-3.5 w-3.5" /> Rechazada
                    </Button>
                  </>
                )}
                {!bloqueada && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => setABorrar(o)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {editando && (
        <LclOfertaDialog
          open
          ofertaId={editando}
          onOpenChange={(v) => !v && setEditando(null)}
          onChanged={() => {
            cargar();
            onChanged?.();
          }}
        />
      )}

      <AlertDialog open={!!aBorrar} onOpenChange={(v) => !v && setABorrar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar la oferta?</AlertDialogTitle>
            <AlertDialogDescription>
              {aBorrar && (
                <>
                  Se elimina <strong>{aBorrar.numero}</strong> v{aBorrar.version} y su
                  PDF. No se puede deshacer.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={borrar}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
